import { TextField } from "@mui/material";
import * as React from "react";
import { DatePicker } from "@mui/x-date-pickers";
import { useTranslation } from "react-i18next";
import { useRecoilValue, useSetRecoilState } from "recoil";
import * as Yup from "yup";
import AdvancedFilter from "../../../component/AdvancedFilter";
import {
  addFilter,
  getFilterValue,
  renderMultiSelect,
} from "../../../utils/FilterUtils";
import {
  ID_FILTER_DEALERS,
  ID_FILTER_LEGAL_ENTITIES,
  ID_FILTER_REPORT_CREATIONDATEFROM,
  ID_FILTER_REPORT_CREATIONDATETO,
} from "../constants/IntermediariesFilters";
import { STATE_REPORT_ACTIVE_FILTERS } from "../constants/IntermediariesRecoil";
import { GlobalContext } from "../../../../../util/state/GlobalContextProvider";
import { useContext } from "react";

function ReportsFilter() {
  const { t } = useTranslation("finance", { keyPrefix: "intermediaries" });
  const { t: tg } = useTranslation("translation");
  const {
    globalData: { user },
  } = useContext(GlobalContext);

  const legalEntityOptions: any[] = user.legalEntities
    ?.filter((x: any) => x.name.toLowerCase() !== "all")
    .map((x: any) => ({
      value: x.name,
      label: tg(`legalEntities.${x.name}`),
    }));

  const dealerOptions: any[] = user.dealers
    ?.filter((x: any) => x.name.toLowerCase() !== "all")
    .map((x: any) => ({
      value: x.name,
      label: tg(`dealers.${x.name}`),
    }));

  const [dealers, setDealers] = React.useState<any[]>(dealerOptions);
  const setFilters = useSetRecoilState(STATE_REPORT_ACTIVE_FILTERS);
  const filterDefs = useRecoilValue(STATE_REPORT_ACTIVE_FILTERS);

  const schema = Yup.object().shape({
    createdDateFrom: Yup.date()
      .nullable()
      .test("testFrom", t("checks.invalidDate"), (value, context) => {
        return (
          !value ||
          !context.parent.createdDateTo ||
          value <= context.parent.createdDateTo
        );
      })
      .typeError(t("checks.invalidDate")),
    createdDateTo: Yup.date()
      .nullable()
      .test("testTo", t("checks.invalidDate"), (value, context) => {
        return (
          !value ||
          !context.parent.createdDateFrom ||
          value >= context.parent.createdDateFrom
        );
      })
      .typeError(t("checks.invalidDate")),
    dealers: Yup.array(),
    legalEntities: Yup.array().min(1, tg("validation.selectAtLeastOne")),
  });

  const defaultValues = {
    createdDateFrom: null,
    createdDateTo: null,
    legalEntities:
      getFilterValue(ID_FILTER_LEGAL_ENTITIES, filterDefs) ||
      legalEntityOptions.map((x: any) => x.value),
    dealers: getFilterValue(ID_FILTER_DEALERS, filterDefs) || [],
  };

  const renderCreationDateFrom = (
    field: any,
    error: any,
    state: any,
    values: any
  ) => (
    <DatePicker
      label={t("filters.creationDateFrom")}
      views={["day"]}
      maxDate={
        values["createdDateTo"] !== null ? values["createdDateTo"] : null
      }
      value={field.value}
      onChange={field.onChange}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          id={"createdDateFrom"}
          error={error !== undefined}
          helperText={error ? error.message : ""}
        />
      )}
      inputFormat={"L"}
    />
  );

  const renderCreationDateTo = (
    field: any,
    error: any,
    state: any,
    values: any
  ) => (
    <DatePicker
      label={t("filters.creationDateTo")}
      views={["day"]}
      minDate={
        values["createdDateFrom"] !== null ? values["createdDateFrom"] : null
      }
      value={field.value}
      onChange={field.onChange}
      renderInput={(params) => (
        <TextField
          {...params}
          fullWidth
          id={"createdDateTo"}
          error={error !== undefined}
          helperText={error ? error.message : ""}
        />
      )}
      inputFormat={"L"}
    />
  );

  const mainFields = {
    createdDateFrom: renderCreationDateFrom,
    createdDateTo: renderCreationDateTo
  };

  const otherFilters = {
    legalEntities: renderMultiSelect(
      tg("shared.legalEntities"),
      legalEntityOptions,
      "legalEntities",
      legalEntityOptions.length <= 1,
      (value: any, setValue: any) => {
        setValue("dealers", []);
        let dealers = user?.dealers
          ?.filter(
            (x: any) =>
              value?.length > 0 &&
              x.legalEntities.some((e: any) => value.includes(e.name)) &&
              x.name.toLowerCase() !== "all"
          )
          .map((x: any) => {
            return {
              label: tg(`dealers.${x.name}`),
              value: x.name,
            };
          });
        setDealers(dealers);
      }
    ),
    dealers: renderMultiSelect(
      tg("shared.dealers"),
      dealers,
      "dealers",
      dealerOptions.length <= 1
    ),
  }

  const submit = (data: any) => {
    addFilter(
      setFilters,
      ID_FILTER_REPORT_CREATIONDATEFROM,
      data.createdDateFrom
    );
    addFilter(setFilters, ID_FILTER_REPORT_CREATIONDATETO, data.createdDateTo);
    addFilter(setFilters, ID_FILTER_LEGAL_ENTITIES, data.legalEntities);
    addFilter(setFilters, ID_FILTER_DEALERS, data.dealers);
  };

  const reset = () => {
    setFilters([]);
  };

  return (
    <AdvancedFilter
      schema={schema}
      mainFields={mainFields}
      fields={otherFilters}
      defaultValues={defaultValues}
      onSubmit={submit}
      onReset={reset}
    />
  );
}

export default ReportsFilter;
